import { LucideIcon } from "lucide-react";
import { CoachPicker } from "@/components/CoachPicker";
import { ThemePicker } from "@/components/ThemePicker";
import { CoachingStyle, AppTheme } from "@/types/evolve";
import { StepHeader } from "./StepHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Eye, EyeOff, UserCircle } from "lucide-react";

interface CustomizationStepProps {
  icon: LucideIcon;
  title: string;
  description: string;
  coachingStyle: CoachingStyle;
  setCoachingStyle: (style: CoachingStyle) => void;
  theme: AppTheme;
  onThemeChange: (updates: Partial<AppTheme>) => void;
  activeTab: 'coach' | 'theme' | 'privacy';
  setActiveTab: (tab: 'coach' | 'theme' | 'privacy') => void;
  displayName: string;
  setDisplayName: (name: string) => void;
  leaderboardPrivacy: string;
  setLeaderboardPrivacy: (privacy: string) => void;
}

export function CustomizationStep({
  icon,
  title,
  description,
  coachingStyle,
  setCoachingStyle,
  theme,
  onThemeChange,
  activeTab,
  setActiveTab,
  displayName,
  setDisplayName,
  leaderboardPrivacy,
  setLeaderboardPrivacy,
}: CustomizationStepProps) {
  return (
    <div className="w-full max-w-sm animate-fade-in space-y-4">
      <StepHeader icon={icon} title={title} description={description} />

      {/* Tab buttons */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('coach')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'coach'
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary/50 text-muted-foreground hover:text-foreground'
          }`}
        >
          🎯 Coach
        </button>
        <button
          onClick={() => setActiveTab('theme')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'theme'
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary/50 text-muted-foreground hover:text-foreground'
          }`}
        >
          🎨 Theme
        </button>
        <button
          onClick={() => setActiveTab('privacy')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'privacy'
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary/50 text-muted-foreground hover:text-foreground'
          }`}
        >
          🔒 Privacy
        </button>
      </div>

      {/* Coach Picker */}
      {activeTab === 'coach' && (
        <CoachPicker
          selected={coachingStyle}
          onChange={setCoachingStyle}
          compact
        />
      )}

      {/* Theme Picker */}
      {activeTab === 'theme' && (
        <div className="max-h-[50vh] overflow-y-auto px-1 -mx-1">
          <ThemePicker
            theme={theme}
            onChange={onThemeChange}
            compact
          />
        </div>
      )}

      {/* Privacy Settings */}
      {activeTab === 'privacy' && (
        <div className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="displayName" className="text-sm font-medium">
              Display Name (optional)
            </Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter a nickname for the leaderboard"
              className="bg-secondary/50 border-border"
            />
            <p className="text-xs text-muted-foreground">
              This will be shown instead of your real name on the leaderboard
            </p>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Leaderboard Display</Label>
            <RadioGroup
              value={leaderboardPrivacy}
              onValueChange={setLeaderboardPrivacy}
              className="space-y-2"
            >
              <label className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 border border-border cursor-pointer hover:bg-secondary/70 transition-colors">
                <RadioGroupItem value="full_name" id="full_name" />
                <Eye className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Full Name</div>
                  <div className="text-xs text-muted-foreground">Show your complete name</div>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 border border-border cursor-pointer hover:bg-secondary/70 transition-colors">
                <RadioGroupItem value="nickname" id="nickname" />
                <UserCircle className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Nickname</div>
                  <div className="text-xs text-muted-foreground">Use your display name instead</div>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 border border-border cursor-pointer hover:bg-secondary/70 transition-colors">
                <RadioGroupItem value="anonymous" id="anonymous" />
                <EyeOff className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Anonymous</div>
                  <div className="text-xs text-muted-foreground">Show as "Anonymous Evolver"</div>
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>
      )}

      <p className="text-xs text-center text-muted-foreground mt-3">
        You can change these anytime in settings
      </p>
    </div>
  );
}
